import { Component } from '@theme/component';

/**
 * Vellatini home hero — reveal-on-load trigger (spec-01 section 8).
 *
 * Sets `data-revealed` on the component root once the page is ready, which flips the
 * `:not([data-revealed])` CSS gate in sections/vellatini-home-hero.liquid so the wordmark
 * letters / CTA blocks / editorial block transition in with their authored stagger. Mirrors
 * the mock's own script: fires on `window.load`, again once `document.fonts.ready` resolves
 * (so the Marcellus/Inter swap doesn't shift layout mid-reveal), and once more after a 500ms
 * fallback in case neither event has fired yet (slow connections, cached-but-not-"loaded"
 * edge cases). Ken Burns and the scroll-cue dot are plain CSS animations and are not gated by
 * this attribute — only the four-part letter/lines/edit reveal is.
 *
 * `prefers-reduced-motion: reduce` reveals immediately and skips the timers entirely; the
 * corresponding CSS also force-disables Ken Burns/the dot/all `[data-anim]` transitions.
 */
class VellatiniHomeHeroComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#initStickyHeader();

    if (this.hasAttribute('data-revealed')) return;

    const reveal = () => {
      this.setAttribute('data-revealed', '');
    };

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      reveal();
      return;
    }

    if (document.readyState === 'complete') {
      reveal();
    } else {
      window.addEventListener('load', reveal, { once: true });
    }

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(reveal).catch(() => {
        /* font loading failures should not block the reveal */
      });
    }

    window.setTimeout(reveal, 500);
  }

  #stickyTeardown = null;

  /**
   * Owner 2026-09-07 (Lark "Website Improvements" Part 3): the hero header is
   * position:fixed; once the hero section has scrolled out from under the row
   * (hero bottom above the row's own height) add `.is-scrolled`, which flips it
   * to the cream/ink shop-header register. Root = .page-wrapper at ≥990 where
   * that element is the scroll container (base.css), the viewport below.
   */
  #initStickyHeader() {
    const header = this.querySelector('.vth-hero__header');
    const hero = this.querySelector('.vth-hero__section');
    if (!header || !hero || !('IntersectionObserver' in window)) return;
    const desktop = window.matchMedia('(min-width: 990px)');
    let io = null;
    const build = () => {
      if (io) io.disconnect();
      const wrapper = document.querySelector('.page-wrapper');
      const root = desktop.matches && wrapper && wrapper.contains(this) ? wrapper : null;
      const offset = Math.round(header.getBoundingClientRect().height) || 0;
      io = new IntersectionObserver(
        (entries) => {
          header.classList.toggle('is-scrolled', !entries[0].isIntersecting);
        },
        { root, rootMargin: `-${offset}px 0px 0px 0px`, threshold: 0 }
      );
      io.observe(hero);
    };
    build();
    desktop.addEventListener('change', build);
    this.#stickyTeardown = () => {
      if (io) io.disconnect();
      desktop.removeEventListener('change', build);
    };
  }

  disconnectedCallback() {
    if (this.#stickyTeardown) this.#stickyTeardown();
    if (super.disconnectedCallback) super.disconnectedCallback();
  }
}

if (!customElements.get('vellatini-home-hero-component')) {
  customElements.define('vellatini-home-hero-component', VellatiniHomeHeroComponent);
}
